package one.healthium.uscreen.ui.components

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.spring
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.wrapContentHeight
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.Modifier
import androidx.compose.ui.hapticfeedback.HapticFeedbackType
import androidx.compose.ui.platform.LocalHapticFeedback
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.RectangleShape
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.em
import androidx.compose.ui.layout.layout
import androidx.compose.ui.platform.LocalDensity
import kotlinx.coroutines.delay

/**
 * An animated calendar wheel that scrolls through a list of date strings
 * using a Spring animation. Clips the visible area to [height] and animates
 * from the first value to the last.
 */
@Composable
fun AnimatableCalendarWheel(
    modifier: Modifier = Modifier,
    datesList: List<String>,
    height: Dp,
    style: TextStyle,
) {
    if (datesList.isEmpty()) return

    Box(
        modifier = modifier.wrapContentHeight(unbounded = true),
    ) {
        Box(
            Modifier
                .clip(RectangleShape)
                .layout { measurable, constraints ->
                    val placeable = measurable.measure(constraints)
                    layout(width = placeable.width, height = height.roundToPx()) {
                        placeable.place(0, 0)
                    }
                }
        ) {
            AnimatableWheel(
                values = datesList,
                height = height.value.toInt(),
                style = style,
            )
        }
    }
}

@Composable
private fun AnimatableWheel(
    values: List<String>,
    height: Int,
    style: TextStyle,
) {
    val itemTarget = remember { Animatable(0f) }
    val haptic = LocalHapticFeedback.current

    LaunchedEffect(key1 = values) {
        delay(500)
        itemTarget.animateTo(
            targetValue = -height * (values.size - 1).toFloat(),
            animationSpec = spring(
                dampingRatio = 0.85f,
                stiffness = 10f,
                visibilityThreshold = 0.5f,
            ),
        )
    }

    // Haptic ticks — 3 ticks per item for frequent tactile feedback
    LaunchedEffect(itemTarget) {
        val tickInterval = (height / 3f).coerceAtLeast(1f)
        var lastTick = 0
        snapshotFlow {
            if (tickInterval > 0) (-itemTarget.value / tickInterval).toInt() else 0
        }.collect { tick ->
            if (tick != lastTick) {
                lastTick = tick
                haptic.performHapticFeedback(HapticFeedbackType.VirtualKey)
            }
        }
    }

    Column(
        modifier = Modifier.offset(y = itemTarget.value.dp),
    ) {
        values.forEach { value ->
            AnimatableWheelItem(
                modifier = Modifier.height(height.dp),
                value = value,
                style = style,
            )
        }
    }
}

@Composable
private fun AnimatableWheelItem(
    modifier: Modifier = Modifier,
    value: String,
    style: TextStyle,
) {
    Text(
        modifier = modifier,
        text = value,
        style = style,
        softWrap = false,
        maxLines = 1,
    )
}

/**
 * Converts a [Dp] value to [TextUnit] in `em` units, relative to the given [fontSize].
 * Useful for sizing InlineTextContent placeholders to match measured composable dimensions.
 */
@Composable
fun dpToEm(dp: Dp, fontSize: TextUnit): TextUnit {
    val density = LocalDensity.current
    val dpInPx = with(density) { dp.toPx() }
    val spInPx = with(density) { fontSize.toPx() }
    return (dpInPx / spInPx).em
}
